import { useSortable } from '@dnd-kit/react/sortable';
import {
  IconAlertTriangle,
  IconEdit,
  IconGripVertical,
  IconTrash,
  IconUsers,
  IconUserShare,
} from '@u21/tabler-icons';
import {
  U21Button,
  U21Spacer,
  U21Tooltip,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { selectAgent } from 'app/modules/session/selectors';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import pluralize from 'pluralize';
import styled from 'styled-components';

import { SavedFilter } from 'app/modules/filters/models';

interface Props {
  index: number;
  isStale: boolean;
  onDelete: (savedFilter: SavedFilter) => void;
  onEdit: (savedFilter: SavedFilter) => void;
  savedFilter: SavedFilter;
}

export const ManageSavedFiltersRow = ({
  index,
  isStale,
  onDelete,
  onEdit,
  savedFilter,
}: Props) => {
  const agent = useSelector(selectAgent);
  const theme = useTheme();
  const isOwner = savedFilter.created_by_id === agent.id;
  const sharedTeamCount = savedFilter.shared_team_ids.length;

  const { ref, isDragging } = useSortable({
    id: savedFilter.id,
    index,
    transition: { duration: 250, easing: 'ease', idle: true },
  });

  return (
    <Row ref={ref} $isDragging={isDragging}>
      <Grip>
        <IconGripVertical />
      </Grip>
      {isStale && (
        <U21Tooltip tooltip="Some filters in this saved filter may be unavailable.">
          <StaleIcon />
        </U21Tooltip>
      )}
      <Title>
        <U21Typography variant="body2" ellipsis>
          {savedFilter.title}
        </U21Typography>
      </Title>
      <U21Spacer horizontal>
        {isOwner && sharedTeamCount > 0 && (
          <U21Tooltip
            tooltip={`Shared with ${pluralize('team', sharedTeamCount, true)}`}
          >
            <IconUserShare color={theme.palette.text.secondary} />
          </U21Tooltip>
        )}
        {isOwner ? (
          <>
            <U21Button
              aria-label={`Edit ${savedFilter.title}`}
              icon={<IconEdit />}
              onClick={() => onEdit(savedFilter)}
              size="small"
            />
            <U21Button
              aria-label={`Delete ${savedFilter.title}`}
              color="error"
              icon={<IconTrash />}
              onClick={() => onDelete(savedFilter)}
              size="small"
            />
          </>
        ) : (
          <U21Tooltip tooltip={`Shared by ${savedFilter.created_by_name}`}>
            <IconUsers color={theme.palette.text.secondary} />
          </U21Tooltip>
        )}
      </U21Spacer>
    </Row>
  );
};

const Row = styled.div<{ $isDragging: boolean }>`
  align-items: center;
  background: ${(props) => props.theme.palette.background.paper};
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 8px;
  cursor: grab;
  display: flex;
  gap: 8px;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
  padding: 8px;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const Grip = styled.div`
  align-items: center;
  color: ${(props) => props.theme.palette.text.secondary};
  display: flex;
`;

const Title = styled.div`
  flex: 1;
  min-width: 0;
`;

const StaleIcon = styled(IconAlertTriangle)`
  color: ${(props) => props.theme.palette.warning.main};
`;
